import type { ReactNode } from "react";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const phases: { stage: string; title: string; description: ReactNode; icon: ReactNode }[] = [
  {
    stage: "PHASE 1",
    title: "Platform back-office (now → 12-18 mo)",
    description: <>Deep partnerships + wide land-and-expand. Prove per-case economics. Build agent capabilities. <B>This is what seed capital funds.</B></>,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
    ),
  },
  {
    stage: "PHASE 2",
    title: "ABS / equity ownership (Series A, 18-36 mo)",
    description: <>Convert Track 2 firms to full back-office clients. Where regulation permits (UK, Australia), <B>take equity</B>. The ABS becomes a platform for roll-up.</>,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
    ),
  },
  {
    stage: "PHASE 3",
    title: "Multi-vertical (Series B+)",
    description: <>Legal → compliance → accounting. <B>Each vertical runs the same dual-track playbook.</B></>,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>
    ),
  },
];

const FunnelArrow = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-accent)]">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export function V3HowItScalesSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[28px] font-light tracking-tight mb-2 font-[family-name:var(--font-heading)]">
        Start as back-office. Graduate to equity.
      </h2>
      <p className="text-[13px] text-[var(--color-fg-secondary)] font-light leading-relaxed mb-5">
        Two tracks during seed. Wide funnel feeds deep conversion at Series A.
      </p>

      {/* Funnel: Track 2 (wide) → Track 1 (narrow) */}
      <div className="grid grid-cols-[1.4fr_auto_1fr] gap-4 items-stretch mb-5">
        <div className="border border-[var(--color-border)] rounded-sm p-3 flex flex-col">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[8px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-[var(--color-fg-secondary)]/10 text-[var(--color-fg-secondary)]">
              TRACK 2 · WIDE
            </span>
          </div>
          <p className="text-[11px] text-[var(--color-fg-secondary)] leading-relaxed">
            Offer low-cost AI services to law firms: website build, branding, design, marketing automation, IT management. Delivered at <B>near-zero marginal cost</B>. Every firm becomes a future Track 1 conversion.
          </p>
        </div>
        <div className="flex items-center justify-center">
          <FunnelArrow />
        </div>
        <div className="border border-[var(--color-accent)]/40 bg-[var(--color-accent)]/5 rounded-sm p-3 flex flex-col">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[8px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-[var(--color-accent)]/20 text-[var(--color-accent)]">
              TRACK 1 · DEEP
            </span>
          </div>
          <p className="text-[11px] text-[var(--color-fg-secondary)] leading-relaxed">
            Build full AI back-office with <B>Raymond and Ratner</B>. Legal agents across categories, compliance agent suite, XENI joint venture. Proves the technology and unit economics.
          </p>
        </div>
      </div>

      {/* Three phases timeline */}
      <div className="pl-5">
        <div className="flex flex-col gap-3.5">
          {phases.map((p, i) => (
            <div key={p.stage} className="relative">
              <div className="absolute left-[-17px] top-[5px] w-2.5 h-2.5 rounded-full shrink-0 z-[1] bg-[var(--color-accent)]" />
              {i < phases.length - 1 && <div className="absolute left-[-13px] top-[15px] bottom-[-18px] w-px bg-[var(--color-border)]" />}
              <div className="flex items-center gap-3 mb-0.5">
                <div className="text-[11px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-accent)] uppercase tracking-[0.1em]">
                  {p.stage} · {p.title}
                </div>
                <span className="text-[var(--color-fg)] shrink-0">{p.icon}</span>
              </div>
              <p className="text-[11px] leading-relaxed text-[var(--color-fg-secondary)] font-light">{p.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light leading-relaxed">
          Track 1 proves the model. Track 2 fills the pipeline. <strong className="font-medium text-[var(--color-fg)]">The playbook is then copy-pasted into every new vertical.</strong>
        </p>
      </div>
    </div>
  );
}
